import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Shield, UserCircle2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { OnboardingTour } from "@/components/OnboardingTour";
import logo from "@/assets/brazzaboost-logo.png";

export const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    checkAdmin();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdmin();
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsLoggedIn(!!user);
    if (user) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      setIsAdmin(!!roles);
    } else {
      setIsAdmin(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b border-border">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center -my-6">
          <img 
            src={logo} 
            alt="BrazzaBoost Logo" 
            className="h-28 md:h-32 w-auto object-contain mix-blend-multiply dark:mix-blend-screen"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-4">
          <Link
            to="/opportunities"
            className="relative text-sm font-medium text-foreground hover:text-primary transition-colors after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-primary after:bottom-0 after:left-0 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200 after:origin-left"
          >
            Opportunités
          </Link>
          <Link
            to="/blog"
            className="relative text-sm font-medium text-foreground hover:text-primary transition-colors after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-primary after:bottom-0 after:left-0 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200 after:origin-left"
          >
            Blog
          </Link>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowTour(true)}
                  className="hover:bg-primary/10"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Guide de démarrage</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
              <Shield className="h-4 w-4 mr-2" />
              Admin
            </Button>
          )}
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-2 border-primary/20 hover:border-primary hover:bg-primary/10"
                  onClick={() => navigate(isLoggedIn ? "/admin" : "/auth")}
                >
                  <UserCircle2 className="h-5 w-5 text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isLoggedIn ? "Tableau de bord" : "Connexion Admin"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button onClick={() => navigate("/publish")}>
            Publier une offre
          </Button>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t">
          <nav className="container py-4 space-y-3">
            <Link
              to="/opportunities"
              className="block text-sm font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Opportunités
            </Link>
            <Link
              to="/blog"
              className="block text-sm font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
            
            <Button 
              variant="outline"
              className="w-full justify-start" 
              onClick={() => {
                setShowTour(true);
                setIsMenuOpen(false);
              }}
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Guide de démarrage
            </Button>
            
            {isAdmin && (
              <Button 
                variant="outline"
                className="w-full" 
                onClick={() => {
                  navigate("/admin");
                  setIsMenuOpen(false);
                }}
              >
                <Shield className="h-4 w-4 mr-2" />
                Admin
              </Button>
            )}
            
            <Button 
              variant="ghost"
              className="w-full justify-start border-2 border-primary/20" 
              onClick={() => {
                navigate(isLoggedIn ? "/admin" : "/auth");
                setIsMenuOpen(false);
              }}
            >
              <UserCircle2 className="h-5 w-5 mr-2 text-primary" />
              {isLoggedIn ? "Tableau de bord" : "Connexion Admin"}
            </Button>
            
            <Button 
              className="w-full" 
              onClick={() => {
                navigate("/publish");
                setIsMenuOpen(false);
              }}
            >
              Publier une offre
            </Button>
          </nav>
        </div>
      )}
      
      <OnboardingTour forceOpen={showTour} onClose={() => setShowTour(false)} />
    </header>
  );
};
